/**
 * YOUTUBE ADS — shared view helpers for the /google-ads/youtube module.
 *
 * Mirrors the helpers the WhisperCRM YouTube workspace kept at module scope
 * (status pills, thumbnails, the click-to-play lightbox) plus the glue that
 * maps WhisperCRM's single "connection" model onto SlightorCRM's multi-account
 * `googleAdsService`: one account (default first, then any with a customer
 * attached) is treated as THE connection for the whole module.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowLeft, Film, Play } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { googleAdsService, GoogleAdsAccount, GoogleAdsCampaignSummary } from '@/services/google-ads.service';

export const YT_BASE = '/google-ads/youtube';
export const YT_SUBTITLE = 'Create and manage YouTube video campaigns, ad groups and ads';

// ── auth ────────────────────────────────────────────────────────────────

export interface YtAuthStatus {
  connected: boolean;
  customer_selected?: boolean;
  customer_id?: string | null;
  customer_name?: string | null;
  currency?: string | null;
}

/** WhisperCRM's `fetchYouTubeAuthStatus`, derived from the Google Ads account list. */
export function useYoutubeAuth() {
  const [auth, setAuth] = useState<YtAuthStatus | null | undefined>(undefined);
  const [account, setAccount] = useState<GoogleAdsAccount | null>(null);

  const checkAuth = useCallback(async () => {
    try {
      const list = (await googleAdsService.list()).filter((a) => a.isActive !== false && !a.isManager);
      const acc =
        list.find((a) => a.isDefault && a.customerId)
        ?? list.find((a) => a.customerId)
        ?? list.find((a) => a.isDefault)
        ?? list[0]
        ?? null;
      setAccount(acc);
      setAuth(acc
        ? {
            connected: true,
            customer_selected: !!acc.customerId,
            customer_id: acc.customerId ?? null,
            customer_name: acc.customerName ?? null,
            currency: acc.currencyCode ?? null,
          }
        : { connected: false });
    } catch {
      setAccount(null);
      setAuth({ connected: false });
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  return { auth, account, accountUid: account?.accountUid ?? '', checkAuth };
}

/** Backend error → inline message. The axios interceptor has already toasted when `e.response` exists. */
export const errMsg = (e: unknown, fallback = 'Something went wrong.'): string => {
  const err = e as { response?: { data?: { message?: string | string[] } }; message?: string } | undefined;
  const m = err?.response?.data?.message;
  if (Array.isArray(m)) return m.join(', ');
  return m ?? err?.message ?? fallback;
};
export const alreadyToasted = (e: unknown) => !!(e as { response?: unknown } | undefined)?.response;

/** `adGroupAds/{adGroupId}~{adId}` — what the pause/resume ad endpoints mutate. */
export const adGroupAdResourceName = (customerId: string, adGroupId: string, adId: string) =>
  `customers/${customerId}/adGroupAds/${adGroupId}~${adId}`;

/** The YouTube module lists video-type campaigns only; the rest live under Campaigns. */
const VIDEO_CHANNEL_TYPES = new Set(['VIDEO', 'DEMAND_GEN']);
export const isYoutubeCampaign = (c: GoogleAdsCampaignSummary) =>
  VIDEO_CHANNEL_TYPES.has(c.advertisingChannelType ?? '');

// ── static option lists (WhisperCRM served these from /static-data) ─────

export type Opt = { value: string; label: string };

/** Video bid strategies the SlightorCRM backend accepts on a VIDEO campaign. */
export const BIDDING_STRATEGIES: Opt[] = [
  { value: 'TARGET_CPV', label: 'Target CPV (cost per view)' },
  { value: 'TARGET_CPM', label: 'Target CPM (reach)' },
  { value: 'MANUAL_CPV', label: 'Manual CPV' },
];
export type BidStrategy = 'MANUAL_CPV' | 'TARGET_CPV' | 'TARGET_CPM';

export const CHANNELS: Opt[] = [
  { value: 'YOUTUBE_IN_STREAM', label: 'YouTube — skippable in-stream' },
  { value: 'YOUTUBE_IN_FEED', label: 'YouTube — in-feed' },
  { value: 'YOUTUBE_SHORTS', label: 'YouTube — Shorts' },
];
export const FORMATS = [
  { v: 'YOUTUBE_IN_STREAM', l: 'Skippable in-stream ads' },
  { v: 'YOUTUBE_IN_FEED', l: 'In-feed ads' },
  { v: 'YOUTUBE_SHORTS', l: 'Shorts ads' },
];
export const formatsToAdFormats = (formats: string[]) => ({
  inStream: formats.includes('YOUTUBE_IN_STREAM'),
  inFeed: formats.includes('YOUTUBE_IN_FEED'),
  shorts: formats.includes('YOUTUBE_SHORTS'),
});

export const pretty = (v?: string | null) =>
  (v || '').toLowerCase().split('_').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');

/** Exactly Google's CallToActionType enum — anything else is rejected. */
export const CALL_TO_ACTIONS: Opt[] = [
  'LEARN_MORE', 'GET_QUOTE', 'APPLY_NOW', 'SIGN_UP', 'CONTACT_US',
  'SUBSCRIBE', 'DOWNLOAD', 'BOOK_NOW', 'SHOP_NOW', 'BUY_NOW',
  'DONATE_NOW', 'ORDER_NOW', 'PLAY_NOW', 'SEE_MORE', 'START_NOW',
  'VISIT_SITE', 'WATCH_NOW',
].map((c) => ({ value: c, label: pretty(c) }));

// ── status ──────────────────────────────────────────────────────────────

export type Tone = 'ok' | 'paused' | 'warn' | 'bad';
const TONE: Record<Tone, string> = {
  ok: 'bg-success-soft text-success',
  paused: 'bg-surface-alt text-text-muted',
  warn: 'bg-warning-soft text-warning',
  bad: 'bg-danger-soft text-danger',
};

/**
 * One pill per row. `secondary` is Google's primary_status (campaign / ad
 * group) or the ad's policy approval — the "why isn't it running" signal that
 * matters more than the ENABLED/PAUSED switch.
 */
export function statusOf(operation: string, secondary?: string | null): { label: string; tone: Tone } {
  const s = (secondary || '').toUpperCase();
  if (s.includes('REMOVED') || operation === 'REMOVED') return { label: 'Removed', tone: 'bad' };
  if (s.includes('DISAPPROVED')) return { label: 'Disapproved', tone: 'bad' };
  if (s.includes('NOT_ELIGIBLE')) return { label: 'Not eligible', tone: 'bad' };
  if (s.includes('MISCONFIGURED')) return { label: 'Misconfigured', tone: 'warn' };
  if (s.includes('UNDER_REVIEW') || s.includes('PENDING') || s.includes('REVIEW_IN_PROGRESS')) return { label: 'In review', tone: 'warn' };
  if (s.includes('LIMITED')) return { label: 'Limited', tone: 'warn' };
  if (s.includes('LEARNING')) return { label: 'Learning', tone: 'warn' };
  if (s.includes('ENDED')) return { label: 'Ended', tone: 'paused' };
  return operation === 'ENABLED' ? { label: 'Active', tone: 'ok' } : { label: 'Paused', tone: 'paused' };
}

/** Ad policy approval → pill, or null when Google has said nothing yet. */
export function approvalOf(approval?: string | null, review?: string | null): { label: string; tone: Tone } | null {
  const a = (approval || '').toUpperCase();
  const r = (review || '').toUpperCase();
  if (a === 'DISAPPROVED') return { label: 'Disapproved', tone: 'bad' };
  if (a === 'APPROVED_LIMITED') return { label: 'Approved · limited', tone: 'warn' };
  if (a === 'AREA_OF_INTEREST_ONLY') return { label: 'Limited reach', tone: 'warn' };
  if (r === 'REVIEW_IN_PROGRESS' || r === 'UNDER_APPEAL') return { label: 'In review', tone: 'warn' };
  if (a === 'APPROVED') return { label: 'Approved', tone: 'ok' };
  return null;
}

/** Google money is in micros (1,000,000 = one currency unit). */
export function formatMicros(micros?: string | number | null, currency?: string | null): string | null {
  if (micros === undefined || micros === null || micros === '') return null;
  const n = Number(micros);
  if (!Number.isFinite(n)) return null;
  const major = n / 1_000_000;
  if (currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(major);
    } catch { /* unknown ISO code — fall through */ }
  }
  return `${currency ? `${currency} ` : ''}${major.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

/** Google's BiddingStrategyType → short label for a pill. */
export function biddingLabel(type?: string | null): string | null {
  if (!type) return null;
  const map: Record<string, string> = {
    TARGET_CPV: 'Target CPV', MANUAL_CPV: 'Manual CPV', TARGET_CPM: 'Target CPM', MANUAL_CPM: 'Manual CPM',
    MANUAL_CPC: 'Manual CPC', TARGET_CPA: 'Target CPA', TARGET_ROAS: 'Target ROAS',
    MAXIMIZE_CONVERSIONS: 'Max conversions', MAXIMIZE_CONVERSION_VALUE: 'Max conv. value',
    TARGET_SPEND: 'Max clicks', TARGET_IMPRESSION_SHARE: 'Impression share', FIXED_CPM: 'Fixed CPM',
  };
  return map[type] ?? pretty(type);
}

export const Pill = ({ label, tone, className }: { label: string; tone: Tone; className?: string }) => (
  <span className={clsx('inline-flex items-center rounded-full px-2 py-0.5 text-2xs font-medium', TONE[tone], className)}>
    {label}
  </span>
);

/** Soft tag used for "incomplete" / "ready" / "starts paused" style badges. */
export const Tag = ({ children, tone = 'paused', className }: { children: React.ReactNode; tone?: Tone; className?: string }) => (
  <span className={clsx('inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium', TONE[tone], className)}>
    {children}
  </span>
);

// ── video ───────────────────────────────────────────────────────────────

/** YouTube serves thumbnails off the video id — no API call needed. */
export const thumbOf = (videoId?: string | null, big = false) =>
  videoId ? `https://i.ytimg.com/vi/${videoId}/${big ? 'hqdefault' : 'mqdefault'}.jpg` : null;

/** Click-to-play lightbox. Nothing is downloaded until the dialog opens. */
export function VideoPlayer({ videoId, title, onClose }: { videoId: string; title?: string; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title={title || 'YouTube video'} widthClass="max-w-3xl">
      <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-black">
        <iframe
          className="h-full w-full"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
          title={title || 'YouTube video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </Modal>
  );
}

/** Thumbnail with a hover play affordance. */
export function VideoThumb({ videoId, onPlay, size = 'sm' }: { videoId?: string | null; onPlay?: () => void; size?: 'sm' | 'lg' }) {
  const src = thumbOf(videoId, size === 'lg');
  const box = size === 'lg' ? 'aspect-video w-full' : 'h-9 w-16';
  if (!src) {
    return (
      <div className={`${box} flex shrink-0 items-center justify-center rounded-md bg-surface-alt text-text-muted`}>
        <Film size={size === 'lg' ? 22 : 13} />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onPlay?.(); }}
      className={`group/th relative ${box} shrink-0 overflow-hidden rounded-md bg-black`}
      title="Play video"
    >
      <img src={src} alt="" className="h-full w-full object-cover transition-transform group-hover/th:scale-105" />
      <span className="absolute inset-0 flex items-center justify-center bg-black/25 opacity-0 transition-opacity group-hover/th:opacity-100">
        <Play size={size === 'lg' ? 30 : 14} className="fill-white text-white" />
      </span>
    </button>
  );
}

// ── small controls the design system doesn't have ───────────────────────

export const Switch = ({ checked, disabled, onCheckedChange, label }: {
  checked: boolean; disabled?: boolean; onCheckedChange: (v: boolean) => void; label?: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={(e) => { e.stopPropagation(); onCheckedChange(!checked); }}
    className={clsx(
      'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
      checked ? 'border-primary bg-primary' : 'border-border-strong bg-surface-alt',
      disabled && 'cursor-wait opacity-60',
    )}
  >
    <span className={clsx(
      'inline-block h-3.5 w-3.5 rounded-full bg-surface shadow-soft transition-transform',
      checked ? 'translate-x-[18px]' : 'translate-x-0.5',
    )} />
  </button>
);

export const Checkbox = ({ checked, onCheckedChange, className }: {
  checked: boolean; onCheckedChange: (v: boolean) => void; className?: string;
}) => (
  <input
    type="checkbox"
    checked={checked}
    onChange={(e) => onCheckedChange(e.target.checked)}
    className={clsx('h-4 w-4 shrink-0 rounded border-border accent-primary', className)}
  />
);

export const Label = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <span className={clsx('block text-xs font-medium text-text-secondary', className)}>{children}</span>
);

export const ErrorBox = ({ message, className }: { message: string; className?: string }) => (
  <div className={clsx('rounded-xl border border-danger/30 bg-danger-soft p-3', className)}>
    <p className="break-words text-xs text-danger">{message}</p>
  </div>
);

export const Spinner = ({ size = 14, className }: { size?: number; className?: string }) => (
  <span
    aria-hidden
    style={{ width: size, height: size }}
    className={clsx('inline-block shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent', className)}
  />
);

/** Topbar `back` slot pointing somewhere other than the URL parent. */
export const BackTo = ({ to, label = 'Back' }: { to: string; label?: string }) => {
  const nav = useNavigate();
  return (
    <button
      onClick={() => nav(to)}
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
      aria-label={label}
      title={label}
    >
      <ArrowLeft size={16} />
    </button>
  );
};

/** Form section wrapper — MODULE SCOPE, or every keystroke remounts and loses focus. */
export function Section({ title, children, hint, right }: {
  title: string; hint?: string; right?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border bg-surface shadow-soft">
      <div className="flex items-center gap-3 border-b border-border px-5 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-text-primary">{title}</div>
          {hint && <div className="mt-0.5 text-xs text-text-muted">{hint}</div>}
        </div>
        {right}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

/** "7 Sep 2026, 20:19" from an ISO string or Google's "YYYY-MM-DDTHH:MM:SS" (account time). */
export function formatWhen(v?: string | null): string | null {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v.slice(0, 16).replace('T', ' ');
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Newest first: by createdAt when known, else by Google's (monotonic) id. */
export function newestFirst<T extends { id: string; createdAt?: string }>(list: T[]): T[] {
  const byId = (a: string, b: string) => (a.length === b.length ? (a < b ? -1 : a > b ? 1 : 0) : a.length - b.length);
  return [...list].sort((a, b) => {
    if (a.createdAt && b.createdAt && a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? 1 : -1;
    return byId(b.id, a.id);
  });
}

/** One line: "Created 7 Sep 2026, 20:19 · Updated 8 Sep 2026, 01:11 · built here". */
export function CampaignDates({ c }: { c: { createdAt?: string; updatedAt?: string; createdVia?: 'crm' | 'google' } }) {
  const created = formatWhen(c.createdAt);
  const updated = formatWhen(c.updatedAt);
  if (!created && !updated) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 text-2xs text-text-muted">
      {created && <span>Created {created}</span>}
      {updated && updated !== created && <><span aria-hidden>·</span><span>Updated {updated}</span></>}
      {c.createdVia === 'crm' && <><span aria-hidden>·</span><span className="text-primary/80">built here</span></>}
    </span>
  );
}
